import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plug, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { EMAIL_REPLY_DELAY_MINUTES } from '@/Domain/components/Integration/Email/email.const';
import { replyDelayLabel } from '@/Domain/components/Integration/Email/replyDelayLabel';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationConnectDialog } from '@/Domain/components/Integration/components/IntegrationConnectDialog';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationControlField } from '@/Domain/components/Integration/components/IntegrationControlField';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { HubSpotIcon } from '@/Domain/components/icons/HubSpot.icon';
import { useIntegrationConnectFunnel } from '@/Domain/hooks/Integration/useIntegrationConnectFunnel.hook';
import { useWhatsAppSetupStatus } from '@/Domain/hooks/Integration/useWhatsAppSetupStatus.hook';

const HUBSPOT_NS_PREFIX = 'integration:hubspot';
const HUBSPOT_INTEGRATION_NAME = 'hubspot' as const;

const HUBSPOT_QUERY_KEY = [QueryKey.integration.get, HUBSPOT_INTEGRATION_NAME] as const;
const HUBSPOT_TOKEN_STATUS_QUERY_KEY = [QueryKey.integration.hubspotTokenStatus] as const;
const HUBSPOT_PIPELINES_QUERY_KEY = [QueryKey.integration.hubspotPipelines] as const;
const HUBSPOT_WHATSAPP_TEMPLATE_QUERY_KEY = [QueryKey.integration.hubspotWhatsappTemplate] as const;
const HUBSPOT_WHATSAPP_TEMPLATES_LIST_QUERY_KEY = [QueryKey.whatsappTemplates.list] as const;
const AUTO_CLOSE_QUERY_KEY = [QueryKey.integration.autoCloseMinutes] as const;
const HUBSPOT_RESOLUTION_SURVEY_QUERY_KEY = [QueryKey.integration.hubspotResolutionSurvey] as const;
const HUBSPOT_EMAIL_REPLY_DELAY_QUERY_KEY = [QueryKey.integration.hubspotEmailReplyDelay] as const;

const AUTO_CLOSE_VALUES = [null, 5, 10, 30] as const;
type AutoCloseValue = (typeof AUTO_CLOSE_VALUES)[number];

// Radix Select can't bind to the string "null"; use a sentinel for "never close".
const AUTO_CLOSE_NEVER_SENTINEL = '__never__';
// Sentinel used to represent "no template selected" in the Select.
const WHATSAPP_TEMPLATE_NONE_SENTINEL = '__none__';

const HUBSPOT_SUPER_ADMIN_URL =
  'https://knowledge.hubspot.com/settings/hubspot-user-permissions-guide#super-admin';
const LINK_CLASS =
  'text-foreground link-underline underline-offset-2 transition-colors hover:text-muted-foreground';

// Fields on the `params` object must match HubspotIntegrationParams in
// backend/appquickchat/appquickchat/api/integrations/integration_params_schemas.py.
export interface HubspotIntegrationParams {
  hubspot_bot_active: boolean;
  hubspot_code: string;
  hubspot_bot_email: string;
  hubspot_bot_email_new: string;
}

export interface HubspotIntegrationResponse {
  name: 'hubspot';
  params: HubspotIntegrationParams;
}

interface HubspotOauthUrlResponse {
  url: string;
}

interface HubspotTokenStatusResponse {
  has_token: boolean;
  is_full_integration_active: boolean;
  bot_email: string;
}

interface HubspotPipeline {
  id: number;
  label: string;
}

interface HubspotPipelinesResponse {
  pipelines: HubspotPipeline[];
  working_hours_pipeline_id: number;
  outside_working_hours_pipeline_id: number;
}

interface HubspotWhatsappTemplateConfig {
  template_name: string;
  template_language: string;
}

interface WhatsAppTemplateEntry {
  id?: string;
  name: string;
  language: string;
  status?: string;
  category?: string;
}

interface WhatsAppTemplatesResponse {
  templates: WhatsAppTemplateEntry[];
}

interface HubspotResolutionSurveyResponse {
  active: boolean;
}

interface HubspotEmailReplyDelayResponse {
  minutes: number;
}

interface AutoCloseResponse {
  auto_close_minutes: AutoCloseValue;
  valid_values: AutoCloseValue[];
}

const emailSchema = z.object({
  hubspot_bot_email: z.string().email(),
});
type EmailFormValues = z.infer<typeof emailSchema>;

const connectedFormSchema = z.object({
  hubspot_bot_active: z.boolean(),
  hubspot_bot_email_new: z.string().email().or(z.literal('')),
  working_hours_pipeline_id: z.string(),
  outside_working_hours_pipeline_id: z.string(),
  auto_close: z.string(),
  whatsapp_template: z.string(),
});
type ConnectedFormValues = z.infer<typeof connectedFormSchema>;

const autoCloseOptionKey = (value: AutoCloseValue): string =>
  value === null ? AUTO_CLOSE_NEVER_SENTINEL : String(value);

const parseAutoCloseKey = (key: string): AutoCloseValue => {
  if (key === AUTO_CLOSE_NEVER_SENTINEL) return null;
  const num = Number(key);
  return (AUTO_CLOSE_VALUES as readonly AutoCloseValue[]).includes(num as AutoCloseValue)
    ? (num as AutoCloseValue)
    : null;
};

const whatsappTemplateKey = (name: string, language: string): string => {
  if (!name) return WHATSAPP_TEMPLATE_NONE_SENTINEL;
  return `${name}|${language || ''}`;
};

const parseWhatsappTemplateKey = (
  key: string,
): { template_name: string; template_language: string } => {
  if (!key || key === WHATSAPP_TEMPLATE_NONE_SENTINEL) {
    return { template_name: '', template_language: '' };
  }
  const sepIndex = key.indexOf('|');
  if (sepIndex === -1) return { template_name: key, template_language: '' };
  return {
    template_name: key.slice(0, sepIndex),
    template_language: key.slice(sepIndex + 1),
  };
};

export interface HubspotSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const HubspotSheet = ({ open, onOpenChange }: HubspotSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);

  const integrationQuery = useQuery<HubspotIntegrationResponse>({
    queryKey: HUBSPOT_QUERY_KEY,
    enabled: open,
  });

  const params = integrationQuery.data?.params;
  const isConnected = Boolean(params?.hubspot_code);

  const { trackConnectStarted, trackConnectFailed } = useIntegrationConnectFunnel(
    'hubspot',
    isConnected,
  );

  const tokenStatusQuery = useQuery<HubspotTokenStatusResponse>({
    queryKey: HUBSPOT_TOKEN_STATUS_QUERY_KEY,
    enabled: open,
  });

  const pipelinesQuery = useQuery<HubspotPipelinesResponse>({
    queryKey: HUBSPOT_PIPELINES_QUERY_KEY,
    enabled: open && isConnected,
  });

  const autoCloseQuery = useQuery<AutoCloseResponse>({
    queryKey: AUTO_CLOSE_QUERY_KEY,
    enabled: open && isConnected,
  });

  const resolutionSurveyQuery = useQuery<HubspotResolutionSurveyResponse>({
    queryKey: HUBSPOT_RESOLUTION_SURVEY_QUERY_KEY,
    enabled: open && isConnected,
  });

  const emailReplyDelayQuery = useQuery<HubspotEmailReplyDelayResponse>({
    queryKey: HUBSPOT_EMAIL_REPLY_DELAY_QUERY_KEY,
    enabled: open && isConnected,
  });

  const whatsappTemplateConfigQuery = useQuery<HubspotWhatsappTemplateConfig>({
    queryKey: HUBSPOT_WHATSAPP_TEMPLATE_QUERY_KEY,
    enabled: open && isConnected,
  });

  // Only fetch templates for WhatsApp-connected accounts (endpoint 404s otherwise); templates stays [] so the section stays hidden.
  const isWhatsAppConnected = useWhatsAppSetupStatus(open).data?.status === 'connected';
  const whatsappTemplatesQuery = useQuery<WhatsAppTemplatesResponse>({
    queryKey: HUBSPOT_WHATSAPP_TEMPLATES_LIST_QUERY_KEY,
    enabled: open && isConnected && isWhatsAppConnected,
    retry: false,
  });

  const whatsappTemplates = whatsappTemplatesQuery.data?.templates ?? [];
  const showWhatsappSection =
    isConnected && !whatsappTemplatesQuery.isError && whatsappTemplates.length > 0;

  // Dedicated mutation for the enable/disable toggle: standardized
  // "<name> integration enabled/disabled" toast (never "saved") and never closes
  // the sheet.
  const toggleActiveMutation = useMutation<
    unknown,
    Error,
    {
      name: typeof HUBSPOT_INTEGRATION_NAME;
      params: { hubspot_bot_active: boolean; hubspot_bot_email?: string };
    }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (_data, variables) => {
      const name = t(`${HUBSPOT_NS_PREFIX}.title`);
      toast.success(
        variables.params.hubspot_bot_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
      // Returned so the mutation stays pending until the saved bot state is refetched,
      // which the resolution survey switch reads as its prerequisite.
      return queryClient.invalidateQueries({ queryKey: HUBSPOT_QUERY_KEY });
    },
    onError: () => {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.save_failed`));
    },
  });

  const patchPipelinesMutation = useMutation<
    unknown,
    Error,
    { working_hours_pipeline_id?: number; outside_working_hours_pipeline_id?: number }
  >({
    mutationKey: [QueryKey.integration.hubspotPipelinesPatch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HUBSPOT_PIPELINES_QUERY_KEY });
      toast.success(t(`${HUBSPOT_NS_PREFIX}.saved`));
    },
    onError: () => {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.pipelines.save_failed`));
    },
  });

  const patchAutoCloseMutation = useMutation<unknown, Error, AutoCloseValue>({
    mutationKey: [QueryKey.integration.autoCloseMinutesPatch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTO_CLOSE_QUERY_KEY });
    },
    onError: () => {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.save_failed`));
    },
  });

  const patchResolutionSurveyMutation = useMutation<unknown, Error, { active: boolean }>({
    mutationKey: [QueryKey.integration.hubspotResolutionSurveyPatch],
    // Returned, not fired and forgotten: the mutation must stay pending until the refetch
    // lands, or the switch settles against the pre-PATCH cache and visibly jumps back.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: HUBSPOT_RESOLUTION_SURVEY_QUERY_KEY }),
    onError: () => {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.save_failed`));
    },
  });

  const patchEmailReplyDelayMutation = useMutation<unknown, Error, { minutes: number }>({
    mutationKey: [QueryKey.integration.hubspotEmailReplyDelayPatch],
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: HUBSPOT_EMAIL_REPLY_DELAY_QUERY_KEY }),
    onError: () => {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.save_failed`));
    },
  });

  const patchWhatsappTemplateMutation = useMutation<
    unknown,
    Error,
    { template_name: string; template_language: string }
  >({
    mutationKey: [QueryKey.integration.hubspotWhatsappTemplatePatch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HUBSPOT_WHATSAPP_TEMPLATE_QUERY_KEY });
    },
    onError: () => {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.whatsapp.save_failed`));
    },
  });

  // The OAuth URL is fetched on click, then the whole tab redirects to HubSpot.
  // The callback returns the browser to the Channels page with a status the app
  // toasts and reopens this sheet on. An optional email rides in the OAuth state.
  const oauthMutation = useMutation<HubspotOauthUrlResponse, Error, { email?: string } | void>({
    mutationKey: [QueryKey.integration.hubspotOauthMutation],
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: () => {
      trackConnectFailed('oauth_failed');
      toast.error(t(`${HUBSPOT_NS_PREFIX}.oauth_failed`));
    },
  });

  // --- Not-connected form (email only) ---
  const emailForm = useForm<EmailFormValues>({
    mode: 'onChange',
    resolver: zodResolver(emailSchema),
    defaultValues: { hubspot_bot_email: '' },
  });

  // Close the connect dialog if the integration reports connected while it's open.
  useEffect(() => {
    if (isConnected && connectDialogOpen) {
      setConnectDialogOpen(false);
    }
  }, [isConnected, connectDialogOpen]);

  const handleStartOauth = emailForm.handleSubmit((values) => {
    trackConnectStarted();
    // The email travels in the OAuth state so the callback validates it against
    // the portal and binds conversation routing to it.
    oauthMutation.mutate({ email: values.hubspot_bot_email });
  });

  // --- Connected form ---
  // The routing-side email from token-status, not ChatImplementation's copy.
  const connectedEmail = tokenStatusQuery.data?.bot_email ?? '';

  const defaultConnectedValues: ConnectedFormValues = useMemo(
    () => ({
      hubspot_bot_active: params?.hubspot_bot_active ?? false,
      hubspot_bot_email_new: connectedEmail,
      working_hours_pipeline_id: String(pipelinesQuery.data?.working_hours_pipeline_id ?? ''),
      outside_working_hours_pipeline_id: String(
        pipelinesQuery.data?.outside_working_hours_pipeline_id ?? '',
      ),
      auto_close: autoCloseOptionKey(autoCloseQuery.data?.auto_close_minutes ?? null),
      whatsapp_template: whatsappTemplateKey(
        whatsappTemplateConfigQuery.data?.template_name ?? '',
        whatsappTemplateConfigQuery.data?.template_language ?? '',
      ),
    }),
    [
      params,
      connectedEmail,
      pipelinesQuery.data,
      autoCloseQuery.data,
      whatsappTemplateConfigQuery.data,
    ],
  );

  const connectedForm = useForm<ConnectedFormValues>({
    mode: 'onChange',
    resolver: zodResolver(connectedFormSchema),
    defaultValues: defaultConnectedValues,
  });

  useEffect(() => {
    // keepDirtyValues so a late-settling query (pipelines, auto-close) can't
    // wipe an email the user is editing for re-authentication.
    connectedForm.reset(defaultConnectedValues, { keepDirtyValues: true });
  }, [defaultConnectedValues, connectedForm]);

  const handlePipelineChange = useCallback(
    (field: 'working_hours_pipeline_id' | 'outside_working_hours_pipeline_id', value: string) => {
      patchPipelinesMutation.mutate({ [field]: Number(value) });
    },
    [patchPipelinesMutation],
  );

  // Individual auto-close handler.
  const handleAutoCloseChange = useCallback(
    (key: string) => {
      connectedForm.setValue('auto_close', key);
      patchAutoCloseMutation.mutate(parseAutoCloseKey(key));
    },
    [connectedForm, patchAutoCloseMutation],
  );

  // Re-authenticate carrying the (possibly changed) connected email in the OAuth
  // state. Shared by the Reconnect button and the email input's Enter key.
  // An empty email would silently run the AI-Actions-only OAuth branch (token
  // refresh without re-binding routing), so reconnect requires a non-empty one.
  const handleReauthenticate = useCallback(() => {
    const email = connectedForm.getValues('hubspot_bot_email_new').trim();
    if (!email || connectedForm.formState.errors.hubspot_bot_email_new) return;
    oauthMutation.mutate({ email });
  }, [connectedForm, oauthMutation]);

  // Optimistic while the PATCH is in flight: the switch must not snap back to the
  // cached value between the click and the invalidated refetch.
  const resolutionSurveyActive =
    patchResolutionSurveyMutation.isPending && patchResolutionSurveyMutation.variables
      ? patchResolutionSurveyMutation.variables.active
      : (resolutionSurveyQuery.data?.active ?? false);
  // The API rejects enabling a survey on an inactive bot, so mirror that guard here -- on
  // the saved value, not the form's, which the Enable toggle updates before its own PATCH
  // lands and would race this one into a 400. An in-flight Enable PATCH leaves the saved
  // value stale too, so it counts as unsettled. Disabling stays available through all of
  // it, so a survey can never be trapped on by switching the bot off.
  const canEnableResolutionSurvey =
    (Boolean(params?.hubspot_bot_active) && !toggleActiveMutation.isPending) ||
    resolutionSurveyActive;

  const emailReplyDelayMinutes =
    patchEmailReplyDelayMutation.isPending && patchEmailReplyDelayMutation.variables
      ? patchEmailReplyDelayMutation.variables.minutes
      : (emailReplyDelayQuery.data?.minutes ?? 0);
  const isInitialLoading = integrationQuery.isLoading;
  const hasToken = Boolean(tokenStatusQuery.data?.has_token) || isConnected;
  const hasPipelines = Boolean(pipelinesQuery.data && pipelinesQuery.data.pipelines.length > 0);

  const editedEmail = connectedForm.watch('hubspot_bot_email_new');
  const emailError = Boolean(connectedForm.formState.errors.hubspot_bot_email_new);
  const emailEmpty = editedEmail.trim().length === 0;
  const isReauthAsDifferentUser =
    editedEmail.trim().length > 0 && editedEmail.trim() !== connectedEmail;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0 space-y-0"
        tabIndex={undefined}
        data-testid="hubspot-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <HubSpotIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="hubspot-sheet-title">
                  {t(`${HUBSPOT_NS_PREFIX}.title`)}
                </SheetTitle>
                <IntegrationStatusBadge connected={isConnected} />
                <IntegrationEnabledBadge enabled={Boolean(params?.hubspot_bot_active)} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="hubspot-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6 flex-1">
          {isInitialLoading ? (
            <div className="flex items-center justify-center py-12" data-testid="hubspot-loading">
              <Spinner className="size-6 text-foreground-subtle" />
            </div>
          ) : (
            <div className="space-y-6" data-testid="hubspot-form">
              <IntegrationIntroduction
                description={t(`${HUBSPOT_NS_PREFIX}.overview`)}
                prerequisites={(
                  t(`${HUBSPOT_NS_PREFIX}.prerequisites`, { returnObjects: true }) as string[]
                ).map((_, index) => (
                  <Trans
                    key={index}
                    t={t}
                    i18nKey={`${HUBSPOT_NS_PREFIX}.prerequisites.${index}`}
                    components={[
                      <a
                        key="superadmin"
                        href={HUBSPOT_SUPER_ADMIN_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={LINK_CLASS}
                      />,
                    ]}
                  />
                ))}
                docsHref="https://docs.quickchat.ai/channels/hubspot"
              />

              <IntegrationSection
                title={t('integrations:section.connect')}
                data-testid="hubspot-connect-enable-section"
              >
                <div className="flex flex-col gap-3" data-testid="hubspot-connection-card">
                  {isConnected && (
                    <IntegrationConnectionCard
                      data-testid="hubspot-connection-details"
                      details={[]}
                      actions={
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={handleReauthenticate}
                          disabled={oauthMutation.isPending || emailError || emailEmpty}
                          data-testid="hubspot-reauthenticate-button"
                        >
                          <RefreshCw className="size-4" />
                          {isReauthAsDifferentUser
                            ? t(`${HUBSPOT_NS_PREFIX}.email.reconnect_as_user`)
                            : t('integrations:reconnect')}
                        </Button>
                      }
                    >
                      <div className="space-y-1.5" data-testid="hubspot-reauth-email">
                        <Label htmlFor="hubspot-connected-email-input">
                          {t(`${HUBSPOT_NS_PREFIX}.email.label`)}
                        </Label>
                        <Input
                          id="hubspot-connected-email-input"
                          type="email"
                          autoComplete="off"
                          placeholder={t(`${HUBSPOT_NS_PREFIX}.email.placeholder`)}
                          error={emailError}
                          data-testid="hubspot-connected-email-input"
                          className="w-full bg-card"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleReauthenticate();
                            }
                          }}
                          {...connectedForm.register('hubspot_bot_email_new')}
                        />
                        {emailError ? (
                          <p className="text-xs text-destructive" data-testid="hubspot-email-error">
                            {t(`${HUBSPOT_NS_PREFIX}.email.invalid`)}
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            {t(`${HUBSPOT_NS_PREFIX}.email.connected_hint`)}
                          </p>
                        )}
                      </div>
                    </IntegrationConnectionCard>
                  )}

                  {!isConnected && (
                    <Button
                      type="button"
                      variant="dark"
                      size="sm"
                      className="w-full"
                      onClick={() => setConnectDialogOpen(true)}
                      data-testid="hubspot-connect-button"
                    >
                      <Plug className="size-4" />
                      {t('integrations:connect', { name: 'HubSpot' })}
                    </Button>
                  )}

                  {isConnected && !hasToken && (
                    <Alert variant="warning" data-testid="hubspot-no-token-warning">
                      <AlertTriangle className="size-4" />
                      <AlertDescription>
                        {t(`${HUBSPOT_NS_PREFIX}.pipelines.load_failed`)}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </IntegrationSection>

              <IntegrationSection
                title={t('integrations:section.settings')}
                data-testid="hubspot-settings-section"
              >
                <Controller
                  control={connectedForm.control}
                  name="hubspot_bot_active"
                  render={({ field }) => (
                    <IntegrationToggleField
                      id="hubspot-active-toggle"
                      label={t('integrations:enable', { name: 'HubSpot' })}
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        toggleActiveMutation.mutate({
                          name: HUBSPOT_INTEGRATION_NAME,
                          params: {
                            hubspot_bot_active: checked,
                            hubspot_bot_email: params?.hubspot_bot_email ?? '',
                          },
                        });
                      }}
                      disabled={!isConnected || toggleActiveMutation.isPending}
                      data-testid="hubspot-active-toggle"
                    />
                  )}
                />

                {isConnected && (
                  <>
                    <Controller
                      control={connectedForm.control}
                      name="working_hours_pipeline_id"
                      render={({ field }) => (
                        <IntegrationControlField
                          id="hubspot-working-hours-pipeline-select"
                          label={t(`${HUBSPOT_NS_PREFIX}.pipelines.working_hours`)}
                        >
                          <Select
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              handlePipelineChange('working_hours_pipeline_id', value);
                            }}
                            disabled={!isConnected || !hasPipelines}
                          >
                            <SelectTrigger
                              className="w-full"
                              disabled={!isConnected || !hasPipelines}
                              data-testid="hubspot-working-hours-pipeline-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {pipelinesQuery.data?.pipelines.map((p) => (
                                <SelectItem
                                  key={p.id}
                                  value={String(p.id)}
                                  data-testid={`hubspot-working-hours-pipeline-option-${p.id}`}
                                >
                                  {p.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </IntegrationControlField>
                      )}
                    />

                    <Controller
                      control={connectedForm.control}
                      name="outside_working_hours_pipeline_id"
                      render={({ field }) => (
                        <IntegrationControlField
                          id="hubspot-outside-hours-pipeline-select"
                          label={t(`${HUBSPOT_NS_PREFIX}.pipelines.outside_hours`)}
                        >
                          <Select
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              handlePipelineChange('outside_working_hours_pipeline_id', value);
                            }}
                            disabled={!isConnected || !hasPipelines}
                          >
                            <SelectTrigger
                              className="w-full"
                              disabled={!isConnected || !hasPipelines}
                              data-testid="hubspot-outside-hours-pipeline-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {pipelinesQuery.data?.pipelines.map((p) => (
                                <SelectItem
                                  key={p.id}
                                  value={String(p.id)}
                                  data-testid={`hubspot-outside-hours-pipeline-option-${p.id}`}
                                >
                                  {p.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </IntegrationControlField>
                      )}
                    />

                    <Controller
                      control={connectedForm.control}
                      name="auto_close"
                      render={({ field }) => (
                        <IntegrationControlField
                          id="hubspot-auto-close-select"
                          label={t(`${HUBSPOT_NS_PREFIX}.auto_close.label`)}
                        >
                          <Select
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              handleAutoCloseChange(value);
                            }}
                            disabled={!isConnected}
                          >
                            <SelectTrigger
                              className="w-full"
                              disabled={!isConnected}
                              data-testid="hubspot-auto-close-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem
                                value={AUTO_CLOSE_NEVER_SENTINEL}
                                data-testid="hubspot-auto-close-option-never"
                              >
                                {t(`${HUBSPOT_NS_PREFIX}.auto_close.options.never`)}
                              </SelectItem>
                              <SelectItem value="5" data-testid="hubspot-auto-close-option-5">
                                {t(`${HUBSPOT_NS_PREFIX}.auto_close.options.5`)}
                              </SelectItem>
                              <SelectItem value="10" data-testid="hubspot-auto-close-option-10">
                                {t(`${HUBSPOT_NS_PREFIX}.auto_close.options.10`)}
                              </SelectItem>
                              <SelectItem value="30" data-testid="hubspot-auto-close-option-30">
                                {t(`${HUBSPOT_NS_PREFIX}.auto_close.options.30`)}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </IntegrationControlField>
                      )}
                    />

                    <IntegrationToggleField
                      id="hubspot-resolution-survey-toggle"
                      label={t(`${HUBSPOT_NS_PREFIX}.resolution_survey.label`)}
                      description={t(`${HUBSPOT_NS_PREFIX}.resolution_survey.description`)}
                      checked={resolutionSurveyActive}
                      onCheckedChange={(checked) =>
                        patchResolutionSurveyMutation.mutate({ active: checked })
                      }
                      disabled={
                        !isConnected ||
                        !resolutionSurveyQuery.isSuccess ||
                        !canEnableResolutionSurvey ||
                        patchResolutionSurveyMutation.isPending
                      }
                      loading={patchResolutionSurveyMutation.isPending}
                      data-testid="hubspot-resolution-survey-toggle"
                    />

                    <IntegrationControlField
                      id="hubspot-email-reply-delay-select"
                      label={t(`${HUBSPOT_NS_PREFIX}.email_reply_delay.label`)}
                      tooltip={t(`${HUBSPOT_NS_PREFIX}.email_reply_delay.description`)}
                    >
                      <Select
                        value={String(emailReplyDelayMinutes)}
                        onValueChange={(value) =>
                          patchEmailReplyDelayMutation.mutate({ minutes: Number(value) })
                        }
                        disabled={
                          !isConnected ||
                          !emailReplyDelayQuery.isSuccess ||
                          patchEmailReplyDelayMutation.isPending
                        }
                      >
                        <SelectTrigger
                          id="hubspot-email-reply-delay-select"
                          className="w-full"
                          disabled={!isConnected}
                          data-testid="hubspot-email-reply-delay-select"
                        >
                          <SelectValue>{replyDelayLabel(t, emailReplyDelayMinutes)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {EMAIL_REPLY_DELAY_MINUTES.map((minutes) => (
                            <SelectItem
                              key={minutes}
                              value={String(minutes)}
                              data-testid={`hubspot-email-reply-delay-option-${minutes}`}
                            >
                              {replyDelayLabel(t, minutes)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </IntegrationControlField>

                    {showWhatsappSection && (
                      <Controller
                        control={connectedForm.control}
                        name="whatsapp_template"
                        render={({ field }) => (
                          <IntegrationControlField
                            id="hubspot-whatsapp-template-select"
                            label={t(`${HUBSPOT_NS_PREFIX}.whatsapp.template`)}
                            tooltip={t(`${HUBSPOT_NS_PREFIX}.whatsapp.description`)}
                          >
                            <Select
                              value={field.value || WHATSAPP_TEMPLATE_NONE_SENTINEL}
                              onValueChange={(value) => {
                                field.onChange(value);
                                patchWhatsappTemplateMutation.mutate(
                                  parseWhatsappTemplateKey(value),
                                );
                              }}
                              disabled={!isConnected}
                            >
                              <SelectTrigger
                                className="w-full"
                                disabled={!isConnected}
                                data-testid="hubspot-whatsapp-template-select"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem
                                  value={WHATSAPP_TEMPLATE_NONE_SENTINEL}
                                  data-testid="hubspot-whatsapp-template-option-none"
                                >
                                  {t(`${HUBSPOT_NS_PREFIX}.whatsapp.none`)}
                                </SelectItem>
                                {whatsappTemplates.map((tpl) => (
                                  <SelectItem
                                    key={`${tpl.name}-${tpl.language}`}
                                    value={whatsappTemplateKey(tpl.name, tpl.language)}
                                    data-testid={`hubspot-whatsapp-template-option-${tpl.name}-${tpl.language}`}
                                  >
                                    {tpl.name} ({tpl.language})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </IntegrationControlField>
                        )}
                      />
                    )}
                  </>
                )}
              </IntegrationSection>
            </div>
          )}
        </div>

        <IntegrationConnectDialog
          open={connectDialogOpen}
          onOpenChange={setConnectDialogOpen}
          title={t('integrations:connect', { name: 'HubSpot' })}
          description={t(`${HUBSPOT_NS_PREFIX}.description`)}
          submitLabel={t('integrations:connect', { name: 'HubSpot' })}
          submitIcon={<Plug className="size-4" />}
          onSubmit={() => void handleStartOauth()}
          submitDisabled={!emailForm.formState.isValid || oauthMutation.isPending}
          isSubmitting={oauthMutation.isPending}
          data-testid="hubspot-connect-dialog"
        >
          <div className="space-y-1.5">
            <Label htmlFor="hubspot-email-input">{t(`${HUBSPOT_NS_PREFIX}.email.label`)}</Label>
            <Input
              id="hubspot-email-input"
              type="email"
              autoComplete="off"
              placeholder={t(`${HUBSPOT_NS_PREFIX}.email.placeholder`)}
              error={Boolean(emailForm.formState.errors.hubspot_bot_email)}
              data-testid="hubspot-email-input"
              className="w-full"
              {...emailForm.register('hubspot_bot_email')}
            />
          </div>
        </IntegrationConnectDialog>
      </SheetContent>
    </Sheet>
  );
};
